import React, { useState, useMemo } from 'react';
import TaskCard from './TaskCard';
import TaskModal from './TaskModal';
import SearchFilter from '../Common/SearchFilter';
import Button from '../Common/Button';
import FinishModeBanner from '../Common/FinishModeBanner';
import { useTasks } from '../../hooks/useTasks';
import { useCalendars } from '../../contexts/CalendarContext';
import { logTaskDeleted, logTaskCompleted } from '../../services/analytics';

const TaskList = () => {
    const [selectedTask, setSelectedTask] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('view');
    const [searchTerm, setSearchTerm] = useState('');
    const [ownerFilter, setOwnerFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [showArchived, setShowArchived] = useState(false);

    const { selectedCalendar } = useCalendars();
    const { tasks, loading, createTask, updateTask, deleteTask, refreshTasks } = useTasks(selectedCalendar?.id);

    // Get unique owners for filter
    const uniqueOwners = useMemo(() => {
        const owners = tasks.map(task => {
            if (typeof task.owner === 'object') {
                return task.owner.name;
            }
            return task.owner;
        }).filter(Boolean);
        return [...new Set(owners)].sort();
    }, [tasks]);

    // Filter and sort tasks
    const filteredTasks = useMemo(() => {
        let filtered = tasks;

        // Apply archived filter
        if (!showArchived) {
            filtered = filtered.filter(task => !task.archived);
        }

        // Apply search filter
        if (searchTerm) {
            filtered = filtered.filter(task =>
                task.task.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply owner filter
        if (ownerFilter) {
            filtered = filtered.filter(task => {
                const taskOwner = typeof task.owner === 'object' ? task.owner.name : task.owner;
                return taskOwner === ownerFilter;
            });
        }

        // Apply status filter
        if (statusFilter) {
            filtered = filtered.filter(task => (task.status || 'not-started') === statusFilter);
        }

        // Sort by due date
        return filtered.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
    }, [tasks, searchTerm, ownerFilter, statusFilter, showArchived]);

    // Calculate progress statistics
    const taskStats = useMemo(() => {
        const active = tasks.filter(t => !t.archived);
        const completed = active.filter(t => t.completed || t.status === 'completed').length;
        const total = active.length;
        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
        return { completed, total, percentage };
    }, [tasks]);

    const handleAddTask = () => {
        if (!selectedCalendar?.id) {
            alert('Please select a calendar first before adding tasks.');
            return;
        }
        setSelectedTask(null);
        setModalMode('add');
        setIsModalOpen(true);
    };

    const handleTaskClick = (task) => {
        setSelectedTask(task);
        setModalMode('view');
        setIsModalOpen(true);
    };

    const handleEditTask = () => {
        setModalMode('edit');
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedTask(null);
        setModalMode('view');
    };

    const handleSaveTask = async (taskData) => {
        // Ensure we have a selected calendar
        if (modalMode === 'add' && !selectedCalendar?.id) {
            throw new Error('Please select a calendar first.');
        }

        try {
            if (modalMode === 'add') {
                await createTask(taskData, selectedCalendar.id);
            } else if (modalMode === 'edit') {
                await updateTask(selectedTask.id, taskData);
            }

            handleCloseModal();
            refreshTasks();
        } catch (error) {
            // Re-throw the error so TaskForm can catch and display it
            throw error;
        }
    };

    const handleDeleteTask = async () => {
        if (!selectedTask) return;

        if (window.confirm('Are you sure you want to delete this task?')) {
            try {
                await deleteTask(selectedTask.id);
                logTaskDeleted({ priority: selectedTask.priority });
                handleCloseModal();
                refreshTasks();
            } catch (error) {
                console.error('Error deleting task:', error);
                alert('Failed to delete task. Please try again.');
            }
        }
    };

    const handleToggleComplete = async (task) => {
        try {
            const isCompleted = task.completed || task.status === 'completed';
            await updateTask(task.id, {
                completed: !isCompleted,
                status: !isCompleted ? 'completed' : 'in-progress',
                completedAt: !isCompleted ? new Date().toISOString() : null
            });
            if (!isCompleted) {
                logTaskCompleted({ priority: task.priority, has_goal: !!task.goalId });
            }
            refreshTasks();
        } catch (error) {
            console.error('Error toggling task completion:', error);
            alert('Failed to update task. Please try again.');
        }
    };

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">All Tasks</h2>
                <Button onClick={handleAddTask} variant="primary">
                    <span className="flex items-center space-x-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        <span>Add Task</span>
                    </span>
                </Button>
            </div>

            {/* Finish Mode Notification Banner */}
            <FinishModeBanner />

            {/* Progress Bar */}
            {taskStats.total > 0 && (
                <div className="mb-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Progress: {taskStats.completed} of {taskStats.total} completed
                        </span>
                        <span className="text-sm font-bold text-primary-600 dark:text-primary-400">
                            {taskStats.percentage}%
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                        <div
                            className="bg-green-500 h-2.5 rounded-full transition-all duration-300"
                            style={{ width: `${taskStats.percentage}%` }}
                        />
                    </div>
                </div>
            )}

            {/* Search and Filter */}
            <SearchFilter
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                ownerFilter={ownerFilter}
                onOwnerChange={setOwnerFilter}
                owners={uniqueOwners}
            />

            {/* Status Filter and Archive Toggle */}
            <div className="flex flex-wrap gap-3 mb-4">
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                    <option value="">All Statuses</option>
                    <option value="not-started">Not Started</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="blocked">Blocked</option>
                </select>

                <label className="flex items-center space-x-2 px-3 py-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={showArchived}
                        onChange={(e) => setShowArchived(e.target.checked)}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Show Archived</span>
                </label>
            </div>

            {/* Loading State */}
            {loading && (
                <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400" role="status" aria-label="Loading"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-300">Loading tasks...</p>
                </div>
            )}

            {/* Task List */}
            {!loading && filteredTasks.length === 0 && (
                <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                    <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p className="mt-4 text-gray-600 dark:text-gray-300">
                        {searchTerm || ownerFilter ? 'No tasks match your filters' : 'No tasks yet. Create your first task!'}
                    </p>
                </div>
            )}

            {!loading && filteredTasks.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredTasks.map((task) => (
                        <TaskCard
                            key={task.id}
                            task={task}
                            onClick={handleTaskClick}
                            onToggleComplete={handleToggleComplete}
                        />
                    ))}
                </div>
            )}

            {/* Task Modal */}
            {isModalOpen && (
                <TaskModal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    task={selectedTask}
                    mode={modalMode}
                    onSave={handleSaveTask}
                    onEdit={handleEditTask}
                    onDelete={handleDeleteTask}
                />
            )}
        </div>
    );
};

export default TaskList;